import React, { useState, useContext, useEffect } from "react";
import Loader from "../../../components/Loader";
import { UserServiceContext } from "../../../services/user/userService";
import CustomAlert from "../../../components/CustomAlert";

const SubUsersModal = ({ setShowSubUserModal, isEdit, originalData }) => {
  const { createSubUser, updateSubUser, loading } =
    useContext(UserServiceContext);

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    userName: "",
    password: "",
    fullName: "",
    email: "",
    mobilePhone: "",
  });

  const [alertConfig, setAlertConfig] = useState({
    show: false,
    title: "",
    text: "",
    icon: "",
    onConfirm: null,
    onCancel: null,
  });

  useEffect(() => {
    if (originalData) {
      setFormData({
        userName: originalData.userName || "",
        fullName: originalData.fullName || "",
        mobilePhone: originalData.mobile || "",
        email: originalData.email || "",
        id: originalData.id || "",
      });
    }
  }, [originalData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.userName) {
      errors.userName = "მომხმარებლის სახელის ველი აუცილებელია.";
    }
    if (!isEdit && !formData.password) {
      errors.password = "პაროლის ველი აუცილებელია.";
    }

    const password = formData.password ? formData.password.trim() : "";

    if (isEdit) {
      // In edit mode: validate only if a password was provided
      if (
        password &&
        !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{6,})/.test(password)
      ) {
        errors.password =
          "პაროლი უნდა იყოს მინიმ 6 სიმბოლო, უნდა შეიცავდეს მინიმუმ 1 დიდ და 1 პატარა ასოს და 1 ციფრს";
      }
    } else {
      // In create mode: password is required and must satisfy complexity
      if (!password) {
        errors.password = "პაროლის ველი აუცილებელია.";
      } else if (
        !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{6,})/.test(password)
      ) {
        errors.password =
          "პაროლი უნდა იყოს მინიმ 6 სიმბოლო, უნდა შეიცავდეს მინიმუმ 1 დიდ და 1 პატარა ასოს და 1 ციფრს";
      }
    }

    if (!formData.fullName) {
      errors.fullName = "სრული სახელის ველი აუცილებელია.";
    }
    // if (!formData.email) {
    //   errors.email = "ელ-ფოსტის ველი აუცილებელია.";
    // }

    if (!formData.mobilePhone) {
      errors.mobilePhone = "მობილური ტელეფონის ველი აუცილებელია.";
    }

    const phone = formData.mobilePhone.replace(/[^0-9]/g, "");

    if (!phone.trim()) {
      errors.mobilePhone = "ნომერი აუცილებელია";
    } else if (phone.trim().length !== 9) {
      errors.mobilePhone = "არასწორი ნომერი";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) return;

    if (isEdit) {
      // Update existing sub-user
      const dt = { ...formData, mobile: formData.mobilePhone };
      const response = await updateSubUser(dt);
      if (response && !response.success) {
        setAlertConfig({
          show: true,
          title: "შეცდომა",
          text: response.message || "ქვე მომხმარებლის განახლება ვერ მოხერხდა.",
          icon: "error",
          onConfirm: () => setShowSubUserModal(),
        });
      } else {
        setAlertConfig({
          show: true,
          title: "წარმატება",
          text: "ქვე მომხმარებელი წარმატებით განახლდა.",
          icon: "success",
          onConfirm: () => setShowSubUserModal(),
          onCancel: () => setShowSubUserModal(),
        });
      }
    } else {
      // Create new sub-user
      const response = await createSubUser(formData);
      if (response && !response.success) {
        setAlertConfig({
          show: true,
          title: "შეცდომა",
          text: response.message || "ქვე მომხმარებლის დამატება ვერ მოხერხდა.",
          icon: "error",
          onConfirm: () => setShowSubUserModal(),
          onCancel: () => setShowSubUserModal(),
        });
      } else {
        setAlertConfig({
          show: true,
          title: "წარმატება",
          text: "ქვე მომხმარებელი წარმატებით დაემატა.",
          icon: "success",
          onConfirm: () => setShowSubUserModal(),
          onCancel: () => setShowSubUserModal(),
        });
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <CustomAlert {...alertConfig} />
      <div
        className={`fixed p-15 w-full max-w-[800px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]`}
      >
        <div className="py-10 md:px-10 px-[7px] bg-card-color rounded-lg shadow-shadow-lg">
          <div className="my-10 lg:px-20 md:px-10 px-[7px] md:max-h-[80svh] max-h-[60svh] overflow-auto custom-scrollbar">
            <div className="text-[24px]/[30px] font-medium mb-2">
              ქვე მომხმარებლის {isEdit ? "რედაქტირება" : "დამატება"}
            </div>
            <div className="text-[14px]/[20px] font-medium mb-2">
              <form onSubmit={handleSubmit}>
                <div className="form-control mb-15">
                  <label htmlFor="userName" className="form-label">
                    მომხმარებლის სახელი
                  </label>
                  <input
                    type="text"
                    id="userName"
                    name="userName"
                    className="form-input"
                    value={formData.userName}
                    onChange={(e) => handleChange(e)}
                  />
                  {errors.userName && (
                    <div className="text-danger text-[14px]/[20px]">
                      {errors.userName}
                    </div>
                  )}
                </div>
                <div className="form-control mb-15">
                  <label htmlFor="password" className="form-label">
                    პაროლი
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="form-input"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={
                      isEdit ? "დატოვეთ ცარიელი პაროლის შესანახად" : ""
                    }
                  />
                  {errors.password && (
                    <div className="text-danger text-[14px]/[20px]">
                      {errors.password}
                    </div>
                  )}
                </div>

                <div className="form-control mb-15">
                  <label htmlFor="fullName" className="form-label">
                    სრული სახელი
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    className="form-input"
                    value={formData.fullName}
                    onChange={handleChange}
                  />
                  {errors.fullName && (
                    <div className="text-danger text-[14px]/[20px]">
                      {errors.fullName}
                    </div>
                  )}
                </div>

                <div className="form-control mb-15">
                  <label htmlFor="email" className="form-label">
                    ელ-ფოსტა
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <div className="text-danger text-[14px]/[20px]">
                      {errors.email}
                    </div>
                  )}
                </div>

                <div className="form-control mb-15">
                  <label htmlFor="mobilePhone" className="form-label">
                    მობილური ტელეფონი
                  </label>
                  <input
                    type="tel"
                    id="mobilePhone"
                    name="mobilePhone"
                    className="form-input"
                    value={formData.mobilePhone}
                    onChange={handleChange}
                  />
                  {errors.mobilePhone && (
                    <div className="text-danger text-[14px]/[20px]">
                      {errors.mobilePhone}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-10 mt-10">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {isEdit ? "შენახვა" : "დამატება"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowSubUserModal()}
                  >
                    გაუქმება
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div
        onClick={setShowSubUserModal}
        className={`contents-[] fixed z-[5] w-full h-full left-0 top-0 bg-black-50 backdrop-blur-[2px]`}
      ></div>
    </>
  );
};

export default SubUsersModal;
